import {
  getIdWithoutInstance,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  type NumberConstraintsType,
  formatNumberWithConstraints,
} from '@packages/systems/core/utils/NumberUtils';
import {
  type ExpressionElement,
  type Expression,
  EList,
  EElement,
  EText,
  TNumber,
  TText,
} from '@packages/systems/wfdl-ast';
import {
  furthestTypographicElement,
  getClosestRichTextElement,
  isUserComponentInst,
  getCurrentComponentRender,
  getRelativePath,
  CHILDREN_PATH,
} from '@packages/utilities/platform';
import {
  isTypographic,
  isRichText,
  stringType,
} from '@packages/utilities/elementTypeUtils';
import {
  Component,
  Plugins,
  Expressions,
  matchExpression,
  Type,
  Types,
  getMeta,
  type ExpressionPath,
  Variable,
} from '@packages/systems/wfdl';
import {getFieldDefaultExpression} from '@packages/systems/core/component-conversions';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import {generateGuid} from '@packages/utilities/guid';
import {ComponentPropType} from '@packages/shared/states';
import {identifier, qualify} from '@packages/systems/wfdl-Qualified';

// This is used to make code intent clearer and ease transition in the future between defaulting
// to null or an empty array as the ContentEditor, atom/rendering, and user components related code evolves
export const isNativeIdPath = (
  nativeIdPath?: Array<NativeId> | null
): nativeIdPath is string[] => {
  return Array.isArray(nativeIdPath) && nativeIdPath.length > 0;
};

export const getEditableTypographicElement = (
  element: ExpressionElement,
  rootElement: ExpressionElement
): ExpressionElement | null => {
  if (isTypographic(rootElement)) {
    return rootElement;
  }
  return furthestTypographicElement(element, EElement(rootElement));
};

export const getEditableRTEElement = (
  element: ExpressionElement,
  rootElement: ExpressionElement
): ExpressionElement | null => {
  if (isRichText(rootElement)) {
    return rootElement;
  }
  return getClosestRichTextElement(EElement(rootElement), element.id);
};

const ELEMENT_CHILDREN_PATH: ExpressionPath = [
  {in: 'Element'},
  ...CHILDREN_PATH,
];

// if the node is bound to an override field, it returns the node with the overrides applied
// if the node is not bound, it returns the node.
export const getSubtreeNode = ({
  editTargetElement,
  targetNativeIdPath,
  applyFieldOverrides,
  components,
  plugins,
  focusedSymbolNativeIdPath,
}: {
  editTargetElement: ExpressionElement;
  targetNativeIdPath: NativeId[];
  applyFieldOverrides: boolean;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  focusedSymbolNativeIdPath: string[] | undefined;
}): {
  subtree: ExpressionElement;
  componentProp: ComponentPropType;
  fieldSlug: string | null;
} => {
  if (applyFieldOverrides) {
    let element = null;
    let rootExpr = focusedSymbolNativeIdPath
      ? getCurrentComponentRender(components, focusedSymbolNativeIdPath)
      : getCurrentComponentRender(components, []);
    if (!rootExpr) {
      return {subtree: editTargetElement, componentProp: null, fieldSlug: null};
    }
    const relativePath = focusedSymbolNativeIdPath
      ? getRelativePath(targetNativeIdPath, focusedSymbolNativeIdPath)
      : targetNativeIdPath;
    let fieldSlug = null;
    let componentProp: ComponentPropType = null;

    for (const path of relativePath) {
      if (!rootExpr) {
        break;
      }
      element = Expressions.getElementById(
        rootExpr,
        getIdWithoutInstance(path)
      );
      if (!element) {
        break;
      }
      const result = maybeReplaceWithComponentInstanceAndApplyOverrides(
        EElement(element),
        components,
        plugins,
        editTargetElement
      );
      rootExpr = result.expression;
      if (result.componentProp) {
        componentProp = result.componentProp;
      }
      if (result.fieldSlug) {
        fieldSlug = result.fieldSlug;
      }
    }

    if (rootExpr) {
      const childrenExpr = Expressions.getIn(rootExpr, ELEMENT_CHILDREN_PATH);

      if (childrenExpr && !Expressions.isList(childrenExpr)) {
        // converting subtree to the format OCE expects: children must be a list of Elements with String data
        rootExpr = Expressions.setIn(
          rootExpr,
          ELEMENT_CHILDREN_PATH,
          EList([childrenExpr])
        );
      }

      const updatedChildrenExpr = Expressions.getIn(
        rootExpr,
        ELEMENT_CHILDREN_PATH
      );
      if (updatedChildrenExpr && Expressions.isList(updatedChildrenExpr)) {
        // Only replace the children of the editTargetElement as OCE targets the children of the element
        const replacedChildren = Expressions.sparseSetIn(
          Expressions.getElementData(editTargetElement),
          CHILDREN_PATH,
          updatedChildrenExpr
        );
        const subtree = Expressions.setElementData(
          editTargetElement,
          replacedChildren
        );
        return {subtree, componentProp, fieldSlug};
      }
    }
  }
  return {subtree: editTargetElement, componentProp: null, fieldSlug: null};
};

const dynamoPlainTextToListOfElementsType = Variable.qualified(
  qualify(
    Plugins.name('DynamoGateway'),
    identifier('dynamoPlainTextToListOfElements')
  )
);
const dynamoPlainTextToTextType = Variable.qualified(
  qualify(Plugins.name('DynamoGateway'), identifier('dynamoPlainTextToText'))
);
const dynamoRichTextToRichTextChildren = Variable.qualified(
  qualify(
    Plugins.name('DynamoGateway'),
    identifier('dynamoRichTextToRichTextChildren')
  )
);
const dynamoNumberToListOfElementsType = Variable.qualified(
  qualify(
    Plugins.name('DynamoGateway'),
    identifier('dynamoNumberToListOfElements')
  )
);
const dynamoNumberToNumberType = Variable.qualified(
  qualify(Plugins.name('DynamoGateway'), identifier('dynamoNumberToNumber'))
);

/**
 * Extracts the field slug from a universal binding expression.
 * This function checks if the expression is a valid CMS binding for on-canvas editing.
 *
 * @param childrenExpr - The expression to extract the field slug from
 * @returns The field slug if the expression is a valid CMS binding, null otherwise
 */
export const extractFieldSlugFromUniversalBinding = (
  childrenExpr: Expression
): string | null => {
  const deconstructedCallExpr = Expressions.deconstructCall(childrenExpr);
  const lastArg =
    deconstructedCallExpr.args.length > 0
      ? deconstructedCallExpr.args[deconstructedCallExpr.args.length - 1]
      : null;
  // Universal Bindings
  if (lastArg) {
    const variable = Expressions.getVariable(deconstructedCallExpr.fun);
    if (!variable) return null;
    const variableEqualsPartial = Variable.equals(variable);
    // These are the CMS bound fields we allow for On-canvas Editing with the Content Editor.
    if (
      !variableEqualsPartial(dynamoPlainTextToListOfElementsType) &&
      !variableEqualsPartial(dynamoPlainTextToTextType) &&
      !variableEqualsPartial(dynamoRichTextToRichTextChildren) &&
      !variableEqualsPartial(dynamoNumberToListOfElementsType) &&
      !variableEqualsPartial(dynamoNumberToNumberType)
    ) {
      return null;
    }
    const select = Expressions.getSelect(lastArg);
    return select?.prop || null;
  }

  return null;
};

/**
 * Checks if the expression is a Number field universal binding.
 * Used to exclude Number fields from GenAI options.
 */
export const isNumberUniversalBinding = (childrenExpr: Expression): boolean => {
  const deconstructedCallExpr = Expressions.deconstructCall(childrenExpr);
  const variable = Expressions.getVariable(deconstructedCallExpr.fun);
  if (!variable) return false;

  const variableEqualsPartial = Variable.equals(variable);
  return (
    variableEqualsPartial(dynamoNumberToListOfElementsType) ||
    variableEqualsPartial(dynamoNumberToNumberType)
  );
};

const getImplicitFunctionType = (
  fun: Expression,
  plugins: Plugins.PluginsType
) => {
  const variable = Expressions.getVariable(fun);
  if (!variable) {
    return undefined;
  }
  const functionType = Variable.match({
    qualified: (v) => plugins.values.get(v),
    unqualified: () => undefined,
  })(variable);
  return functionType && functionType.envType.implicit !== 'no'
    ? functionType
    : undefined;
};

const getPropertyBindingKey = (
  expr: Expression,
  plugins: Plugins.PluginsType
): string | null => {
  return matchExpression({
    Select: ({from, prop}) => {
      if (Expressions.isDataVariable(from)) {
        return prop;
      }
      return null;
    },
    // We can have a wrapping implicit function that wraps the select expression
    // in these cases we look for the select expression in the argument of the call.
    // Curried: Builtin::numberToListOfElements(-1)(data.myProp)
    // Single-arg: Basic::textToListOfElements(data.myProp)
    Call: ({fun, arg}) => {
      const call = Expressions.getCall(fun);
      const functionType = getImplicitFunctionType(
        call ? call.fun : fun,
        plugins
      );
      if (functionType) {
        return getPropertyBindingKey(arg, plugins);
      }
      return null;
    },
    _: () => null,
  })(expr);
};

const isSingleArgImplicitBindingCall = (
  expr: Expression,
  plugins: Plugins.PluginsType
): boolean => {
  const call = Expressions.getCall(expr);
  if (!call || Expressions.getCall(call.fun)) {
    return false;
  }
  return Boolean(getImplicitFunctionType(call.fun, plugins));
};

/**
 * Applies overrides and defaults from the instance element to the component render
 */
const applyOverridesAndDefaultsFromInstance = (
  instanceElement: ExpressionElement,
  componentRender: Expression,
  component: Component.ComponentType,
  plugins: Plugins.PluginsType,
  editTargetElement: ExpressionElement
): {
  expression: Expression;
  componentProp: ComponentPropType;
  fieldSlug: string | null;
} => {
  let componentProp: ComponentPropType = null;
  const instData = Expressions.getElementData(instanceElement);
  const instDataFields = Expressions.getRecord(instData);
  const typeFields = Types.getRecordFields(Component.getDataType(component));
  if (!typeFields) {
    return {expression: componentRender, componentProp: null, fieldSlug: null};
  }
  const editTargetElementData = Expressions.getElementData(editTargetElement);
  const editTargetElementDataFields = Expressions.getRecord(
    editTargetElementData
  );
  let fieldSlug: string | null = null;

  const putInData = (e: Expression): Expression => {
    const prop = getPropertyBindingKey(e, plugins);
    if (!prop) {
      return e;
    }
    const propValue = instDataFields ? instDataFields[prop] : null;
    const isDynamoBinding = propValue
      ? getDynamoBindingPaths(propValue).length === 1
      : false;
    // If this prop is applied on our eventTargetElement
    const isOnEventTarget =
      editTargetElementDataFields &&
      editTargetElementDataFields['children'] === e;

    // Apply instance override if it exists
    if (propValue) {
      if (isDynamoBinding) {
        const propFieldSlug = extractFieldSlugFromUniversalBinding(propValue);
        if (propFieldSlug && isOnEventTarget) {
          fieldSlug = propFieldSlug;
        }
      } else {
        const propType = typeFields[prop];
        const convertedNumber = maybeConvertNumberToElementString(
          propValue,
          propType
        );

        if (convertedNumber && convertedNumber.element && isOnEventTarget) {
          componentProp = convertedNumber.componentProp;
          return convertedNumber.element;
        }

        const convertedText = maybeConvertTextToElementString(
          propValue,
          propType
        );
        if (convertedText && convertedText.element && isOnEventTarget) {
          componentProp = convertedText.componentProp;
          return convertedText.element;
        }

        // RichText props aren't converted to an element string like Number /
        // PlainText, so tag the context here or the editor treats the prop as
        // plain text and lets governance-gated actions through.
        if (
          isOnEventTarget &&
          propType &&
          Types.isRichTextChildrenType(propType)
        ) {
          componentProp = {type: 'RichText'};
        }
      }
      // Keep Basic::textToListOfElements(...) around CMS / pass-through
      // overrides so everywhere() can still substitute the inner Select.
      if (isSingleArgImplicitBindingCall(e, plugins)) {
        return e;
      }
      return propValue;
    }

    // Apply the default from the root component type
    const propType = typeFields[prop];
    if (propType) {
      const defaultValue = getFieldDefaultExpression(
        component,
        prop,
        propType,
        plugins
      );
      const convertedNumber = maybeConvertNumberToElementString(
        defaultValue,
        propType
      );
      if (convertedNumber && isOnEventTarget) {
        componentProp = convertedNumber.componentProp;
        return convertedNumber.element;
      }
      const convertedText = maybeConvertTextToElementString(
        defaultValue,
        propType
      );
      if (convertedText && convertedText.element && isOnEventTarget) {
        componentProp = convertedText.componentProp;
        return convertedText.element;
      }
      // Same reason as the override branch above: RichText values skip string
      // conversion, so the default value needs the tag too.
      if (isOnEventTarget && Types.isRichTextChildrenType(propType)) {
        componentProp = {type: 'RichText'};
      }
      if (isSingleArgImplicitBindingCall(e, plugins)) {
        return e;
      }
      return defaultValue;
    }

    // Unknown property, return the original expression
    return e;
  };

  return {
    expression: Expressions.everywhere(putInData)(componentRender),
    componentProp,
    fieldSlug,
  };
};

const maybeConvertNumberToElementString = (
  expr: Expression,
  propType?: Type | null
): {element: Expression; componentProp: ComponentPropType} | null => {
  if (Expressions.isNumber(expr)) {
    const numberValue = Expressions.getNumber(expr);
    let displayValue = String(numberValue ?? '');

    let componentProp: ComponentPropType = null;

    // Extract validation constraints if we have type information
    if (propType && Types.equal(propType, TNumber)) {
      const meta = getMeta(propType)?.value;
      if (meta) {
        const constraints: NumberConstraintsType = {
          minValue: meta?.min,
          maxValue: meta?.max,
          format: meta?.precision === 0 ? 'integer' : 'decimal',
          precision: meta?.precision,
        };

        componentProp = {
          type: 'Number',
          constraints,
        };

        // Apply formatting constraints to display value
        if (numberValue !== null && numberValue !== undefined) {
          displayValue = formatNumberWithConstraints(numberValue, constraints);
        }
      } else {
        componentProp = {
          type: 'Number',
        };
      }
    }

    const element = EElement({
      id: generateGuid(),
      type: stringType,
      data: EText(displayValue),
    });

    return {element, componentProp};
  }
  return null;
};

const maybeConvertTextToElementString = (
  expr: Expression,
  propType?: Type | null
): {element: Expression; componentProp: ComponentPropType} | null => {
  if (!Expressions.isText(expr) || !propType || !Types.equal(propType, TText)) {
    return null;
  }

  const textValue = Expressions.getText(expr) ?? '';
  const element = EElement({
    id: generateGuid(),
    type: stringType,
    data: EText(textValue),
  });

  return {
    element,
    componentProp: {type: 'PlainText'},
  };
};

const maybeReplaceWithComponentInstanceAndApplyOverrides = (
  expr: Expression,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  editTargetElement: ExpressionElement
): {
  expression: Expression;
  componentProp: ComponentPropType;
  fieldSlug: string | null;
} => {
  const el = Expressions.getElement(expr);
  if (!el || !isUserComponentInst(componentMap, el)) {
    return {expression: expr, componentProp: null, fieldSlug: null};
  }
  const rootComponent = componentMap.get(el.type);
  if (!rootComponent) {
    return {expression: expr, componentProp: null, fieldSlug: null};
  }
  return applyOverridesAndDefaultsFromInstance(
    el,
    Component.getRender(rootComponent),
    rootComponent,
    plugins,
    editTargetElement
  );
};
